import { format, parseISO } from "date-fns";

export default function validateHyperlink(str) {
  if (!str || typeof str !== "string") {
    return "";
  }
  const trimmed = str.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.indexOf("http://") === 0 || trimmed.indexOf("https://") === 0) {
    return trimmed
  }
  return `https://${trimmed}`;

}

export const formatShortDisplayDate = (date) => {
  if (!date) {
    return "";
  }

  return format(parseISO(date), 'MM/dd/yyyy');
}

// US ZIP code: 5 digits (22150) or ZIP+4 (22150-1234). Mirrors the server-side rule in
// acr-api PrimaryPocService.ValidatePrimaryPocFields so client and API agree on what saves.
export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
export const ZIP_CODE_INVALID_MESSAGE = "Enter a valid ZIP code: 5 digits (22150) or ZIP+4 (22150-1234).";

// ZIP is optional, but when supplied it must match the format. Returns an error message or null.
export const validateZipCode = (zipCode) => {
  const trimmed = String(zipCode ?? "").trim();
  if (trimmed && !ZIP_CODE_REGEX.test(trimmed)) {
    return ZIP_CODE_INVALID_MESSAGE;
  }
  return null;
};

export const formatPrimaryPoc = (street1, street2, city, state, zipCode) => {
  if (street2 != null && street2 !== "N/A") {
    return `${street1}, ${street2}, ${city}, ${state} ${zipCode}`;
  }
  return `${street1}, ${city}, ${state} ${zipCode}`;
}

export const formatUTCDisplayDate = (date) => {
  if (!date) {
    return "";
  }
  // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
  // This ensures the date displays consistently regardless of time components or timezone
  const dateMatch = date.match(/^\d{4}-\d{2}-\d{2}/);
  if (!dateMatch) {
    return "";
  }
  // Parse just the date part (without time) and format it
  const parsed = parseISO(dateMatch[0]);
  return format(parsed, "MMM do, yyyy");
}

export const formatDisplayDate = (date) => {
  if (!date) {
    return "";
  }
  const parsed = parseISO(date);

  return `${format(parsed, "MMM do, yyyy")}`;
}

function normalizeApiErrorArray(items) {
  if (!Array.isArray(items)) {
    return [];
  }
  return items
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }
      return item?.errorMessage || item?.message || item?.description || item?.title || item?.error;
    })
    .filter(Boolean);
}

function normalizeApiErrorObject(payload) {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const direct = [
    payload.errorMessage,
    payload.message,
    payload.description,
    payload.title,
    payload.error,
  ].filter((value) => typeof value === "string" && value.trim());

  const modelStateValues = payload.errors && typeof payload.errors === "object"
    ? Object.values(payload.errors).flatMap((value) => (Array.isArray(value) ? value : [value]))
    : [];
  const modelState = modelStateValues
    .filter((value) => typeof value === "string" && value.trim());

  return [...direct, ...modelState];
}

export const getApiErrorMessage = (errorOrResponse, fallback = "Error") => {
  const payload = errorOrResponse?.response?.data ?? errorOrResponse?.data ?? errorOrResponse;

  const arrayMessages = normalizeApiErrorArray(payload);
  if (arrayMessages.length) {
    return arrayMessages.join(" ");
  }

  if (typeof payload === "string" && payload.trim()) {
    return payload.trim();
  }

  const objectMessages = normalizeApiErrorObject(payload);
  if (objectMessages.length) {
    return objectMessages.join(" ");
  }

  return fallback;
};

export const extractStatus = (response) => {
  if (response.status < 300) {
    return "Ok";
  }
  if (response.status === 401 || response.status === 403) {
    return "Unauthorized";
  }
  if (response.status === 400) {
    return getApiErrorMessage(response, "Bad Request");
  }

  return getApiErrorMessage(response, "Error");
}
